import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Segmented, Select, Space, Typography } from 'antd';
import { ExportOutlined, ReloadOutlined } from '@ant-design/icons';
import { previewScreenConfig, previewScreenVersion, type ScreenPreview } from '@/api/screens';
import { normalizeApiError } from '@/api/errors';
import { readThemeAsset } from '@/api/theme';

const { Text } = Typography;

export const ROOM_PREVIEW_URL = '/room/preview.html';
const READY = 'ol-room-preview:ready';
const RENDER = 'ol-room-preview:render';
const FRAME_WAIT_MS = 5000;
const DEBOUNCE_MS = 500;
const WIDTHS = { Desktop: '100%', Phone: 390 };

interface Props {
  configId: string;
  /** The unsaved definition to show, or the stored version: readers can see only what is stored. */
  source: { definition: unknown } | { version: number };
  /** The languages to offer. */
  locales: string[];
}

type PreviewAssets = Record<string, { bytes: ArrayBuffer; mediaType: string }>;

function payloadOf(preview: ScreenPreview, assets: PreviewAssets) {
  return {
    locale: preview.locale,
    tasks: preview.tasks,
    journey: preview.journey,
    theme: preview.theme,
    reviewLabel: preview.reviewLabel,
    assets,
  };
}

/** Every image and font the live theme names. */
function themeAssetIds(theme: unknown): string[] {
  const brand = (theme as { brand?: Record<string, unknown> } | null)?.brand;
  if (!brand) return [];
  const design = brand.design as { fonts?: { assetId?: string }[] } | undefined;
  return [
    brand.markAssetId,
    brand.wordmarkAssetId,
    brand.faviconAssetId,
    ...(design?.fonts ?? []).map((f) => f.assetId),
  ].filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/**
 * The preview is the room: the deployed room bundle's preview page, in a frame or its own window, showing
 * what the server would serve for this definition. It is never a console drawing of the room. A
 * definition the gate refuses is not rendered; the last good one stays up, labelled.
 */
export function RoomPreview({ configId, source, locales }: Props) {
  const frame = useRef<HTMLIFrameElement>(null);
  const popup = useRef<Window | null>(null);
  const shown = useRef<ReturnType<typeof payloadOf> | null>(null);
  const [locale, setLocale] = useState(locales[0] ?? 'en');
  const [width, setWidth] = useState<keyof typeof WIDTHS>('Desktop');
  const [connected, setConnected] = useState(false);
  const [frameBlocked, setFrameBlocked] = useState(false);
  const [errors, setErrors] = useState(0);
  const [failure, setFailure] = useState<string | null>(null);
  const [assetsMissing, setAssetsMissing] = useState(false);
  // Theme assets do not change between previews. Each is read once, with this user's bearer.
  const assets = useRef<PreviewAssets>({});

  const offered = useMemo(() => (locales.length > 0 ? locales : ['en']), [locales]);
  useEffect(() => {
    if (!offered.includes(locale)) setLocale(offered[0]);
  }, [offered, locale]);

  const post = useCallback(() => {
    if (!shown.current) return;
    const message = { type: RENDER, payload: shown.current };
    const origin = window.location.origin;
    frame.current?.contentWindow?.postMessage(message, origin);
    if (popup.current && !popup.current.closed) popup.current.postMessage(message, origin);
  }, []);

  // The page says when it is listening. Only this origin, and only the window we opened or framed.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== frame.current?.contentWindow && event.source !== popup.current) return;
      if ((event.data as { type?: string } | null)?.type === READY) {
        setConnected(true);
        setFrameBlocked(false);
        post();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [post]);

  // A frame that never says ready: the deployment does not allow framing, or has no preview page.
  useEffect(() => {
    if (connected) return undefined;
    const id = setTimeout(() => setFrameBlocked(true), FRAME_WAIT_MS);
    return () => clearTimeout(id);
  }, [connected]);

  useEffect(() => () => popup.current?.close(), []);

  useEffect(() => {
    let current = true;
    const id = setTimeout(() => {
      const asked =
        'version' in source
          ? previewScreenVersion(configId, source.version, locale)
          : previewScreenConfig(configId.trim() || 'draft', source.definition, locale);
      asked
        .then(async (result) => {
          if (!current) return;
          setFailure(null);
          setErrors(result.validation.diagnostics.filter((d) => d.severity === 'ERROR').length);
          if (!result.validation.ok) return;
          // The room's asset routes need a session. So the page gets the bytes, and its own loaders run on them.
          const ids = themeAssetIds(result.theme).filter((id) => !assets.current[id]);
          const read = await Promise.all(ids.map((id) => readThemeAsset(id).catch(() => null)));
          read.forEach((asset, i) => {
            if (asset) assets.current[ids[i]] = asset;
          });
          if (!current) return;
          setAssetsMissing(read.some((asset) => !asset));
          shown.current = payloadOf(result, assets.current);
          post();
        })
        .catch((e) => {
          if (current) setFailure(normalizeApiError(e).message);
        });
    }, DEBOUNCE_MS);
    return () => {
      current = false;
      clearTimeout(id);
    };
  }, [configId, source, locale, post]);

  const openWindow = () => {
    popup.current = window.open(ROOM_PREVIEW_URL, 'ol-room-preview', 'width=480,height=860');
  };

  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }} data-testid="room-preview">
      <Space wrap>
        <Select
          aria-label="Preview language"
          data-testid="preview-locale"
          value={locale}
          style={{ width: 90 }}
          options={offered.map((l) => ({ value: l, label: l }))}
          onChange={setLocale}
        />
        <Segmented
          aria-label="Preview width"
          options={Object.keys(WIDTHS)}
          value={width}
          onChange={(v) => setWidth(v as keyof typeof WIDTHS)}
        />
        <Button icon={<ReloadOutlined />} onClick={post} data-testid="preview-restart">
          Start over
        </Button>
        <Button icon={<ExportOutlined />} onClick={openWindow} data-testid="preview-window">
          Open in a window
        </Button>
        <Text type="secondary" style={{ fontSize: 12 }}>
          The real room. Nothing you do here is saved or sent; codes are shown on screen.
        </Text>
      </Space>
      {errors > 0 && (
        <Alert
          type="error"
          showIcon
          data-testid="preview-stale"
          message={`The gate refuses this definition (${errors}), so the room is not showing it`}
          description={shown.current ? 'This is the last version that passed.' : 'Fix what it lists and the room will show it.'}
        />
      )}
      {failure && <Alert type="error" showIcon message={failure} />}
      {assetsMissing && (
        <Alert
          type="info"
          showIcon
          data-testid="preview-assets-missing"
          message="Your role cannot read this hub's theme assets, so the preview shows the default mark and the system font."
        />
      )}
      {frameBlocked && !connected && (
        <Alert
          type="info"
          showIcon
          data-testid="preview-framing"
          message="This deployment does not let the room show in a frame"
          description="Open it in its own window instead. Applying deploy/room-preview to the web server allows the frame."
          action={
            <Button size="small" type="primary" onClick={openWindow}>
              Open in a window
            </Button>
          }
        />
      )}
      {!frameBlocked && (
        <iframe
          ref={frame}
          title="Room preview"
          src={ROOM_PREVIEW_URL}
          data-testid="preview-frame"
          style={{
            width: WIDTHS[width],
            height: 720,
            border: '1px solid var(--ol-border)',
            borderRadius: 8,
            display: 'block',
          }}
        />
      )}
    </Space>
  );
}
